import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  type OnChanges,
  type OnInit,
  output,
  type SimpleChanges,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { updateOrAdd } from '@core/helpers/helpers';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { IbanPipe } from '@fino-ui/finances';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { FinoSelectModule } from '@fino-ui/select';
import { FinoTranslationModule, TranslateService } from '@fino-ui/translation';
import { clone, cloneDeep, filter, isEmpty, map, merge, pick } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import {
  EXECUTION_DAY,
  FINANCE_CURRENCY_CODES,
  FINANCE_INTERVAL_TYPES,
  STANDING_ORDER_FIELDS,
  TERMINUEBERWEISUNG,
} from './standing-orders.constants';

@Component({
  selector: 'app-standing-orders',
  templateUrl: './standing-orders.component.html',
  styleUrls: ['./standing-orders.component.sass'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    FinoSelectModule,
    ButtonModule,
    FinoCommonModule,
    FinoTranslationModule,
    FinoIconModule,
    DatePipe,
    IbanPipe,
  ],
})
export class StandingOrdersComponent implements OnInit, OnChanges {
  public readonly translateService = inject(TranslateService);

  public readonly isTerminueberweisung = signal(false);

  executionDayItems = EXECUTION_DAY;
  intervalTypeItems = this.mapSelectTypes(FINANCE_INTERVAL_TYPES);
  currencyCodesItems = FINANCE_CURRENCY_CODES;
  standingOrderFields = STANDING_ORDER_FIELDS;

  readonly intervalSelect = viewChild<any>('intervalSelect');
  readonly executionDaySelect = viewChild<any>('executionDaySelect');

  public readonly toggleAllDelete = signal(true);
  datePipe = new DatePipe('de-DE');

  public searchForm: SubmittableForm = new SubmittableForm({
    search: new FormControl('', []),
  });

  datePickerConfig = {
    adaptivePosition: true,
    containerClass: 'theme-default',
    dateInputFormat: 'DD.MM.YYYY',
  };

  public readonly form = input<SubmittableForm>(new SubmittableForm({}));

  public readonly standingOrders = input<any[]>([]);

  public readonly filteredStandingOrders = signal<any[]>([]);

  readonly standingOrdersSubmitted = output<any>();

  public readonly isUpdateMode = signal(false);

  get hasIban() {
    return !isEmpty(this.form().controls.iban?.value);
  }

  get hasComment() {
    return this.form().controls.comment.value;
  }

  ngOnChanges(changes: SimpleChanges) {
    const { standingOrders } = changes;
    if (standingOrders) {
      this.filteredStandingOrders.set(clone(this.standingOrders()));
    }
  }

  transformToDate(date: string) {
    if (moment(date, 'DD.MM.YYYY').isValid())
      return moment(date, 'DD.MM.YYYY').toDate();
    return date;
  }

  submit() {
    this.form().markAllAsTouched();
    if (this.form().valid || this.hasComment) {
      const toBeAdded = this.form().value;
      if (!toBeAdded.id) {
        toBeAdded.id = uuidv4();
      }
      this.isUpdateMode.set(false);

      updateOrAdd(
        this.standingOrders(),
        merge(toBeAdded, { selected: true }),
        'id',
      );
      this.filteredStandingOrders.set(clone(this.standingOrders()));
      this.searchForm.reset();

      this.standingOrdersSubmitted.emit(this.form().value);
      this.form().reset();
      this.initForm();

      this.intervalSelect()?.select.handleClearClick();
      this.executionDaySelect()?.select.handleClearClick();

      this.form().updateValueAndValidity();
    }
  }

  readonly hasStandingOrders = computed(() => {
    const standingOrders = this.standingOrders();
    return standingOrders && standingOrders.length > 0;
  });

  deleteItem(item: any, selected: boolean = true) {
    item.selected = selected;
    this.standingOrdersSubmitted.emit(this.form().value);
  }

  setupFilterForm() {
    this.searchForm.controls.search.valueChanges.subscribe(
      (keyword: string) => {
        if (!keyword) {
          this.filteredStandingOrders.set(clone(this.standingOrders()));
          return;
        }
        this.filteredStandingOrders.set(
          filter(this.standingOrders(), (item) =>
            item.name.toLowerCase().includes(keyword.toLowerCase()),
          ),
        );
      },
    );
  }

  duplicateItem(item: any) {
    const cloned = cloneDeep(item);
    cloned.id = uuidv4();
    this.applyModelToForm(cloned);
  }

  deleteAllItems() {
    this.toggleAllDelete.set(!this.toggleAllDelete());
    map(this.standingOrders(), (d) =>
      this.deleteItem(d, this.toggleAllDelete()),
    );
  }

  get isFormValid() {
    return this.form().valid;
  }

  editItem(item: any) {
    this.isUpdateMode.set(true);
    this.applyModelToForm(item);
  }

  applyModelToForm(item: any) {
    const pickedItems = pick(item, this.standingOrderFields);

    // TODO: can be removed just for legacy support
    if (
      pickedItems.firstExecution &&
      !moment(pickedItems.firstExecution, 'DD.MM.YYYY').isValid()
    ) {
      pickedItems.firstExecution = this.datePipe.transform(
        pickedItems.firstExecution,
      );
    }
    if (
      pickedItems.lastExecution &&
      !moment(pickedItems.lastExecution, 'DD.MM.YYYY').isValid()
    ) {
      pickedItems.lastExecution = this.datePipe.transform(
        pickedItems.lastExecution,
      );
    }

    if (
      pickedItems.nextExecution &&
      !moment(pickedItems.nextExecution, 'DD.MM.YYYY').isValid()
    ) {
      pickedItems.nextExecution = this.datePipe.transform(
        pickedItems.nextExecution,
      );
    }
    this.form().patchValue(pickedItems);

    this.intervalSelect()?.setValue(pickedItems.interval);
    this.executionDaySelect()?.setValue(parseInt(pickedItems.executionDay, 10));
  }

  mapSelectTypes(types: any) {
    return map(types, (type) => {
      return {
        label: this.translateService.instant(type),
        value: type,
      };
    });
  }

  initForm() {
    this.form().controls.currency?.setValue('EUR');
  }

  ngOnInit() {
    this.initForm();
    this.setupFilterForm();
    this.form().controls.iban?.valueChanges.subscribe((r) => {
      this.setRequiredForField('bic', isEmpty(r));
    });

    this.form().controls.interval?.valueChanges.subscribe((r) => {
      this.isTerminueberweisung.set(TERMINUEBERWEISUNG === r);
      this.setRequiredForField('firstExecution', !this.isTerminueberweisung());
      this.setRequiredForField('executionDay', !this.isTerminueberweisung());
    });
  }

  setRequiredForField(field: string, required: boolean) {
    this.form()
      .get(field)
      ?.setValidators(required ? [Validators.required] : []);
    this.form().get(field)?.updateValueAndValidity();
  }
}
